import type { CommonColors } from '@mui/material/styles';

import type { PaletteColorNoChannels } from './core/palette';
import type { ThemeColorScheme, ThemeCssVariables, ThemeDirection } from './types';

// ----------------------------------------------------------------------

type ThemeConfig = {
  classesPrefix: string;
  modeStorageKey: string;
  direction: ThemeDirection;
  defaultMode: ThemeColorScheme;
  cssVariables: ThemeCssVariables;
  fontFamily: Record<'primary' | 'secondary', string>;
  palette: Record<'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error', PaletteColorNoChannels> & {
    common: Pick<CommonColors, 'black' | 'white'>;
    grey: Record<'50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900', string>;
  };
};

export const themeConfig: ThemeConfig = {
  /** **************************************
   * Base
   *************************************** */
  direction: 'ltr',
  defaultMode: 'light',
  modeStorageKey: 'theme-mode',
  classesPrefix: 'minimal',
  /** **************************************
   * Typography
   *************************************** */
  fontFamily: {
    primary: '"Helvetica Neue", "Inter Variable", "Open Sans", Helvetica, Arial, sans-serif',
    secondary: '"Helvetica Neue", "Inter Variable", "Open Sans", Helvetica, Arial, sans-serif',
  },
  /** **************************************
   * Palette
   *************************************** */
  palette: {
    primary: {
      lighter: '#EDEFFE',
      light: '#9AA5FF',
      main: '#4C5DF9',
      dark: '#3A49D6',
      darker: '#2A36A8',
      contrastText: '#FFFFFF',
    },
    secondary: {
      lighter: '#CAFDEB',
      light: '#61F4D9',
      main: '#00DCDA',
      dark: '#00849E',
      darker: '#004569',
      contrastText: '#FFFFFF',
    },
    info: {
      lighter: '#E4F1F4',
      light: '#63C7D6',
      main: '#2DB2C7',
      dark: '#0F6577',
      darker: '#093F4B',
      contrastText: '#FFFFFF',
    },
    success: {
      lighter: '#E3F3E8',
      light: '#69C48F',
      main: '#157F49',
      dark: '#12703F',
      darker: '#0B4A2A',
      contrastText: '#FFFFFF',
    },
    warning: {
      lighter: '#FEF4E4',
      light: '#E0B75C',
      main: '#D69E2E',
      dark: '#8A5B12',
      darker: '#5C3C0A',
      contrastText: '#1F2020',
    },
    error: {
      lighter: '#FDEEEE',
      light: '#F08A85',
      main: '#E14B45',
      dark: '#C93B36',
      darker: '#8F2622',
      contrastText: '#FFFFFF',
    },
    grey: {
      '50': '#FBFBFB',
      '100': '#F9F9F9',
      '200': '#F1F1F1',
      '300': '#E7E7E8',
      '400': '#D4D4D4',
      '500': '#8D8F8F',
      '600': '#6A6C6C',
      '700': '#3B3B40',
      '800': '#1F2020',
      '900': '#0A0A0A',
    },
    common: { black: '#000000', white: '#FFFFFF' },
  },
  /** **************************************
   * Css variables
   *************************************** */
  cssVariables: {
    cssVarPrefix: '',
    colorSchemeSelector: 'data-color-scheme',
  },
};
